import { Button, Spinner, StickyBarContent, StickyBarWrapper } from '@common/components';
import {
  CloseModalButton,
  FormWrapper,
  Header,
  InnerScroller,
  LeftSideBarWrapper,
  MobileHeading,
  Modal,
  PrevStepButton,
  RightSideBarWrapper,
  SplitBarWrapper,
  Title,
} from '@common/components/BookingForms';
import { FormattedLink } from '@common/components/FormattedLink';
import { usePageContext } from '@common/components/PageContext';
import { Routes } from '@common/constants';
import { SignIn } from '@features/SignIn';
import {
  invalidateSpaPackageTimeslots,
  useGetSpaPackage,
  useGetSpaPackageTimeslots,
} from '@features/spa';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { TranslationKey, useTranslations } from '@lang';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { useQueryClient } from '@tanstack/react-query';
import Cookie from 'js-cookie';
import moment from 'moment';
import { FC, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { Price } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import { SpaPackageType } from 'shared-types/SpaPackage';
import { SpaPackageFormValues, buildSpaPackageFormSchema } from '../SpaPackageForm.schema';
import { calculateExtraPrices } from './SpaPackageModal.helpers';
import { SpaPackageAvailability } from './SpaPackageAvailability/SpaPackageAvailability';
import { SpaPackageExtras } from './SpaPackageExtras/SpaPackageExtras';
import { SubHeader } from './SpaPackageModal.styles';
import { SpaPackageBasketResolveType, SpaPackageFormDataType } from './SpaPackageModal.types';
import { parseSpaPackageData } from './SubmitButton/SubmitButton.helpers';

type SpaPackageModalProps = {
  entity: SpaPackageType;
  bookingStep: string;
  language: string;
  property: Property;
  handleCloseModal: (showMiniBasket: boolean) => void;
  startDate: string;
};

const calculatePrice = (entityPrice: Price, entityCount: number, extrasTotal: number): Price => ({
  ...entityPrice,
  amount: entityPrice.amount * entityCount + extrasTotal,
});

export const SpaPackageModal: FC<SpaPackageModalProps> = ({
  entity,
  bookingStep,
  language,
  property,
  handleCloseModal,
  startDate,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { routingData } = usePageContext();

  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const currentBasketItems = useSelector(
    (state) => (basketSelectors.getBasketDetails(state) as any)?.items || []
  );
  const isFetchingAvailability = useSelector((state) =>
    (basketSelectors.getIsBasketDetailsFetching as any)(state)
  );

  const isGroupedSpaBookingEnabled = useFeatureIsOn('spa-days-grouped-booking');

  const { name, id: spaDayId, telephoneOnly } = entity;

  const form = useForm<SpaPackageFormValues>({
    resolver: zodResolver(buildSpaPackageFormSchema(entity)),
    defaultValues: {
      date: moment(journeyParams.spaDate).format('YYYY-MM-DD'),
      guests: Array.from({ length: entity.numberOfGuests }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender: '',
      })),
    },
    mode: 'onChange',
  });

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedExtras = useWatch({ control: form.control, name: 'extras' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  const [unavailable, setUnavailable] = useState(false);

  const { data: spaPackage, isFetching: isFetchingSpaPackage } = useGetSpaPackage(
    property.id,
    spaDayId,
    language,
    false,
    selectedDateString
  );

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    spaDayId,
    language,
    selectedDateString
  );

  const { data: timeslots } = useGetSpaPackageTimeslots(
    property.id,
    entity.id,
    startDate,
    entity.numberOfGuests,
    7,
    !unavailable
  );

  const selectedDateTimeSlots = timeslots?.[selectedDateString];

  const discount = selectedDateTimeSlots?.find(
    (slot) => slot.startTime === watchedArrivalTime?.startTime
  )?.discount;

  const price = selectedDateTimeSlots?.find(
    (slot) => slot.startTime === watchedArrivalTime?.startTime
  )?.price;

  const guestsCount = watchedGuests?.length || 0;
  const extrasTotal = calculateExtraPrices(
    Object.fromEntries(
      Object.entries(watchedExtras || {}).filter(([, v]) => Array.isArray(v))
    ) as any
  );

  const addToBasket = (data: SpaPackageFormValues) => {
    const cleanedExtras = Object.fromEntries(
      Object.entries(data.extras || {}).filter(([, v]) => Array.isArray(v) && v.length > 0)
    );
    const cleanedData = { ...data, extras: cleanedExtras };
    const parsedBasketItems = parseSpaPackageData(
      cleanedData as unknown as SpaPackageFormDataType,
      entity,
      isGroupedSpaBookingEnabled
    );
    const promises: SpaPackageBasketResolveType[] = [];
    if (currentBasketItems.length) {
      const basketId = Cookie.get('basketId') as string;
      const items = [...currentBasketItems, ...parsedBasketItems];
      promises.push(
        dispatch(
          updateBasket(property.id, basketId, language, { items }, false, true) as any
        ) as SpaPackageBasketResolveType
      );
    } else {
      promises.push(
        dispatch(
          createBasket(property.id, language, { items: parsedBasketItems }, true) as any
        ) as SpaPackageBasketResolveType
      );
    }
    Promise.any(promises)
      .then(() => {
        invalidateSpaPackageTimeslots(queryClient);
        handleCloseModal(true);
      })
      .catch(() => {
        // Basket errors are handled by redux — keep the modal open so the user can retry
      });
  };

  const handleSubmit = form.handleSubmit(addToBasket);

  const handleNextStep = form.handleSubmit(() => {
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id: spaDayId, bookingStep: 'extras' },
    });
  });

  const goToDetailPage = () =>
    router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id: spaDayId } });

  const goToAvailabilityPage = () =>
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id: spaDayId, bookingStep: 'availability' },
    });

  const AVAILABILITY_KEY = 'availability';
  const EXTRAS_KEY = 'extras';

  const steps: Record<
    string,
    {
      form: React.ReactNode;
      stickyButton: JSX.Element | null;
      goToPreviousStep: (() => void) | null;
      titleId: TranslationKey | '';
    }
  > = {
    [AVAILABILITY_KEY]: {
      form: (
        <SpaPackageAvailability
          spaPackageId={spaDayId}
          property={property}
          setUnavailable={setUnavailable}
          language={language}
          maxGuests={spaPackage?.maximumGuestsPerBasket || Infinity}
          unavailable={unavailable}
        />
      ),
      stickyButton: extras?.length ? (
        <Button
          onClick={handleNextStep}
          isPrimary
          disabled={!watchedArrivalTime || unavailable || !form.formState.isValid}
        >
          {t('spa.package.nextStep')}
        </Button>
      ) : (
        <Button isPrimary onClick={handleSubmit} disabled={unavailable || !form.formState.isValid}>
          {t('product.ctaButton')}
        </Button>
      ),
      goToPreviousStep: goToDetailPage,
      titleId: 'spa.package.availabilityTitle',
    },
    [EXTRAS_KEY]: {
      form: <SpaPackageExtras extras={extras} />,
      stickyButton: (
        <Button isPrimary onClick={handleSubmit}>
          {t('booking.continue')}
        </Button>
      ),
      goToPreviousStep: goToAvailabilityPage,
      titleId: 'spa.package.extrasTitle',
    },
    '': {
      form: null,
      stickyButton: null,
      goToPreviousStep: null,
      titleId: '',
    },
  };

  const currentStep = steps[bookingStep] || steps[''];

  if (!bookingStep || currentStep.form === null) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <Modal isVisible>
        <>
          <Spinner showDelayMessage isActive={isFetchingAvailability || isFetchingSpaPackage} />
          <Header>
            <PrevStepButton onClick={currentStep.goToPreviousStep ?? undefined} />
            <MobileHeading>{name}</MobileHeading>
            <CloseModalButton onClick={() => handleCloseModal(false)} />
          </Header>
          <InnerScroller scrollToTopIndicator={bookingStep}>
            <FormWrapper>
              <SubHeader>
                {routingData && (
                  <FormattedLink format="button" href={routingData.href}>
                    {routingData.message}
                  </FormattedLink>
                )}
              </SubHeader>
              {currentStep.titleId && <Title titleId={currentStep.titleId} />}
              <SplitBarWrapper>
                <LeftSideBarWrapper>{currentStep.form}</LeftSideBarWrapper>
                <RightSideBarWrapper>
                  <SignIn />
                </RightSideBarWrapper>
              </SplitBarWrapper>
            </FormWrapper>
          </InnerScroller>
          {!telephoneOnly && (
            <StickyBarWrapper>
              <StickyBarContent
                name={entity.name}
                displayedPrice={calculatePrice(price || entity.price, guestsCount, extrasTotal)}
                discountedPrice={
                  discount
                    ? {
                        ...discount,
                        discountedPrice: calculatePrice(
                          discount.discountedPrice,
                          guestsCount,
                          extrasTotal
                        ),
                      }
                    : undefined
                }
                button={currentStep.stickyButton ?? <></>}
              />
            </StickyBarWrapper>
          )}
        </>
      </Modal>
    </FormProvider>
  );
};
